import {
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";

import {
  AlertTriangle,
  Boxes,
  History,
  LoaderCircle,
  MinusCircle,
  PackageCheck,
  PackageX,
  PlusCircle,
  Search
} from "lucide-react";

import {
  useMemo,
  useState
} from "react";

import {
  adjustAdminInventory,
  getAdminInventory,
  getAdminInventoryMovements
} from "../services/admin";

import type {
  AdminInventoryProduct,
  AdminInventoryReason
} from "../types/admin";

const reasons: {
  value: AdminInventoryReason;
  label: string;
}[] = [
  {
    value: "RESTOCK",
    label: "Restock"
  },
  {
    value: "CORRECTION",
    label: "Stock correction"
  },
  {
    value: "DAMAGE",
    label: "Damaged / write-off"
  },
  {
    value: "RETURN",
    label: "Customer return"
  },
  {
    value: "OTHER",
    label: "Other"
  }
];

function stockState(
  product: AdminInventoryProduct
) {
  if (
    product.stockQuantity <= 0
  ) {
    return {
      label: "Out of stock",
      className:
        "bg-red-50 text-red-700"
    };
  }

  if (
    product.stockQuantity <=
    product.lowStockThreshold
  ) {
    return {
      label: "Low stock",
      className:
        "bg-amber-50 text-amber-700"
    };
  }

  return {
    label: "In stock",
    className:
      "bg-[#edf1e8] text-[#526746]"
  };
}

export function AdminInventoryPage() {
  const queryClient =
    useQueryClient();

  const [search, setSearch] =
    useState("");

  const [
    selectedProduct,
    setSelectedProduct
  ] =
    useState<
      AdminInventoryProduct |
      null
    >(null);

  const [
    quantityChange,
    setQuantityChange
  ] =
    useState("");

  const [reason, setReason] =
    useState<AdminInventoryReason>(
      "RESTOCK"
    );

  const [note, setNote] =
    useState("");

  const inventoryQuery =
    useQuery({
      queryKey: [
        "admin",
        "inventory",
        search
      ],

      queryFn: () =>
        getAdminInventory(
          search.trim() ||
          undefined
        )
    });

  const movementsQuery =
    useQuery({
      queryKey: [
        "admin",
        "inventory",
        "movements"
      ],

      queryFn: () =>
        getAdminInventoryMovements(
          50
        )
    });

  const adjustMutation =
    useMutation({
      mutationFn: () => {
        if (!selectedProduct) {
          throw new Error(
            "No product selected"
          );
        }

        return adjustAdminInventory(
          selectedProduct.id,
          {
            quantityChange:
              Number(
                quantityChange
              ),

            reason,

            note:
              note.trim() ||
              null
          }
        );
      },

      onSuccess: async () => {
        setSelectedProduct(
          null
        );

        setQuantityChange("");
        setReason("RESTOCK");
        setNote("");

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [
              "admin",
              "inventory"
            ]
          }),

          queryClient.invalidateQueries({
            queryKey: [
              "admin",
              "inventory",
              "movements"
            ]
          }),

          queryClient.invalidateQueries({
            queryKey: [
              "admin",
              "products"
            ]
          })
        ]);
      }
    });

  const products =
    inventoryQuery.data
      ?.products ??
    [];

  const summary =
    inventoryQuery.data
      ?.summary;

  const movements =
    movementsQuery.data ??
    [];

  const projectedStock =
    useMemo(
      () => {
        if (
          !selectedProduct ||
          !quantityChange
        ) {
          return null;
        }

        const change =
          Number(
            quantityChange
          );

        if (
          !Number.isFinite(
            change
          )
        ) {
          return null;
        }

        return (
          selectedProduct
            .stockQuantity +
          change
        );
      },
      [
        selectedProduct,
        quantityChange
      ]
    );

  function openAdjustment(
    product:
      AdminInventoryProduct,
    mode:
      "add" |
      "remove"
  ) {
    setSelectedProduct(
      product
    );

    setQuantityChange(
      mode === "add"
        ? "1"
        : "-1"
    );

    setReason(
      mode === "add"
        ? "RESTOCK"
        : "CORRECTION"
    );

    setNote("");
  }

  return (
    <div className="space-y-8">
      <div>
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#70845b]">
          Stock control
        </p>

        <h1 className="mt-2 font-serif text-4xl text-[#38251e]">
          Inventory
        </h1>

        <p className="mt-3 max-w-2xl text-[#746d65]">
          Track product stock,
          identify low-stock items,
          restock products and review
          inventory movement history.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-2xl bg-white p-5">
          <Boxes
            size={22}
            className="text-[#70845b]"
          />

          <p className="mt-4 text-3xl font-semibold text-[#38251e]">
            {summary?.totalProducts ??
              0}
          </p>

          <p className="mt-1 text-sm text-[#746d65]">
            Products
          </p>
        </div>

        <div className="rounded-2xl bg-white p-5">
          <PackageCheck
            size={22}
            className="text-[#70845b]"
          />

          <p className="mt-4 text-3xl font-semibold text-[#38251e]">
            {summary?.totalUnits ??
              0}
          </p>

          <p className="mt-1 text-sm text-[#746d65]">
            Units in stock
          </p>
        </div>

        <div className="rounded-2xl bg-white p-5">
          <AlertTriangle
            size={22}
            className="text-[#a27a4d]"
          />

          <p className="mt-4 text-3xl font-semibold text-[#38251e]">
            {summary?.lowStock ??
              0}
          </p>

          <p className="mt-1 text-sm text-[#746d65]">
            Low stock
          </p>
        </div>

        <div className="rounded-2xl bg-white p-5">
          <PackageX
            size={22}
            className="text-red-600"
          />

          <p className="mt-4 text-3xl font-semibold text-[#38251e]">
            {summary?.outOfStock ??
              0}
          </p>

          <p className="mt-1 text-sm text-[#746d65]">
            Out of stock
          </p>
        </div>
      </div>

      <div className="rounded-[2rem] bg-white p-5">
        <label className="relative block">
          <Search
            size={18}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-[#746d65]"
          />

          <input
            value={search}
            onChange={(event) =>
              setSearch(
                event.target.value
              )
            }
            placeholder="Search product, SKU or category"
            className="w-full rounded-xl border border-[#5e3b2c]/15 bg-[#fffdf8] py-3 pl-11 pr-4 text-sm"
          />
        </label>
      </div>

      {inventoryQuery.isLoading && (
        <div className="flex min-h-60 items-center justify-center">
          <LoaderCircle
            size={36}
            className="animate-spin text-[#70845b]"
          />
        </div>
      )}

      {inventoryQuery.isError && (
        <div className="rounded-2xl bg-red-50 p-6 text-red-700">
          Inventory could not be loaded.
        </div>
      )}

      {!inventoryQuery.isLoading &&
        !inventoryQuery.isError && (
          <div className="overflow-hidden rounded-[2rem] bg-white">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[850px]">
                <thead>
                  <tr className="border-b border-[#5e3b2c]/10 bg-[#fffaf4] text-left text-xs uppercase tracking-[0.12em] text-[#70845b]">
                    <th className="px-6 py-4">
                      Product
                    </th>

                    <th className="px-6 py-4">
                      SKU
                    </th>

                    <th className="px-6 py-4">
                      Category
                    </th>

                    <th className="px-6 py-4 text-center">
                      Stock
                    </th>

                    <th className="px-6 py-4">
                      Status
                    </th>

                    <th className="px-6 py-4 text-right">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {products.map(
                    (product) => {
                      const state =
                        stockState(
                          product
                        );

                      return (
                        <tr
                          key={
                            product.id
                          }
                          className="border-b border-[#5e3b2c]/5"
                        >
                          <td className="px-6 py-5">
                            <p className="font-semibold text-[#38251e]">
                              {
                                product.name
                              }
                            </p>

                            <p className="mt-1 text-xs text-[#746d65]">
                              Low stock alert:
                              {" "}
                              {
                                product.lowStockThreshold
                              }
                            </p>
                          </td>

                          <td className="px-6 py-5 text-sm text-[#746d65]">
                            {
                              product.sku
                            }
                          </td>

                          <td className="px-6 py-5 text-sm text-[#746d65]">
                            {
                              product
                                .category
                                .name
                            }
                          </td>

                          <td className="px-6 py-5 text-center">
                            <span className="text-2xl font-semibold text-[#38251e]">
                              {
                                product
                                  .stockQuantity
                              }
                            </span>
                          </td>

                          <td className="px-6 py-5">
                            <span className={[
                              "rounded-full px-3 py-1.5 text-xs font-semibold",
                              state.className
                            ].join(" ")}>
                              {
                                state.label
                              }
                            </span>
                          </td>

                          <td className="px-6 py-5">
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  openAdjustment(
                                    product,
                                    "add"
                                  )
                                }
                                className="inline-flex items-center gap-2 rounded-full bg-[#edf1e8] px-4 py-2 text-xs font-semibold text-[#526746]"
                              >
                                <PlusCircle
                                  size={15}
                                />

                                Restock
                              </button>

                              <button
                                type="button"
                                onClick={() =>
                                  openAdjustment(
                                    product,
                                    "remove"
                                  )
                                }
                                className="inline-flex items-center gap-2 rounded-full border border-[#5e3b2c]/15 px-4 py-2 text-xs font-semibold text-[#5e3b2c]"
                              >
                                <MinusCircle
                                  size={15}
                                />

                                Adjust
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    }
                  )}

                  {products.length ===
                    0 && (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-6 py-16 text-center text-[#746d65]"
                      >
                        No matching products.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

      <section className="rounded-[2rem] bg-white p-6">
        <div className="flex items-center gap-3">
          <History
            size={22}
            className="text-[#70845b]"
          />

          <div>
            <h2 className="font-serif text-2xl text-[#38251e]">
              Inventory history
            </h2>

            <p className="mt-1 text-sm text-[#746d65]">
              Recent manual stock adjustments.
            </p>
          </div>
        </div>

        {movementsQuery.isLoading ? (
          <div className="flex min-h-40 items-center justify-center">
            <LoaderCircle
              size={28}
              className="animate-spin text-[#70845b]"
            />
          </div>
        ) : (
          <div className="mt-6 overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b border-[#5e3b2c]/10 text-left text-xs uppercase tracking-[0.1em] text-[#70845b]">
                  <th className="pb-3">
                    Date
                  </th>

                  <th className="pb-3">
                    Product
                  </th>

                  <th className="pb-3">
                    Reason
                  </th>

                  <th className="pb-3 text-center">
                    Previous
                  </th>

                  <th className="pb-3 text-center">
                    Change
                  </th>

                  <th className="pb-3 text-center">
                    New
                  </th>

                  <th className="pb-3">
                    Note
                  </th>
                </tr>
              </thead>

              <tbody>
                {movements.map(
                  (movement) => (
                    <tr
                      key={
                        movement.id
                      }
                      className="border-b border-[#5e3b2c]/5 text-sm"
                    >
                      <td className="py-4 text-[#746d65]">
                        {new Date(
                          movement.createdAt
                        ).toLocaleString(
                          "en-IN"
                        )}
                      </td>

                      <td className="py-4">
                        <p className="font-medium text-[#38251e]">
                          {
                            movement.productName
                          }
                        </p>

                        <p className="text-xs text-[#746d65]">
                          {
                            movement.sku
                          }
                        </p>
                      </td>

                      <td className="py-4">
                        {
                          movement.reason
                        }
                      </td>

                      <td className="py-4 text-center">
                        {
                          movement.previousStock
                        }
                      </td>

                      <td className={[
                        "py-4 text-center font-semibold",
                        movement.quantityChange >
                        0
                          ? "text-green-700"
                          : "text-red-700"
                      ].join(" ")}>
                        {movement.quantityChange >
                        0
                          ? "+"
                          : ""}
                        {
                          movement.quantityChange
                        }
                      </td>

                      <td className="py-4 text-center font-semibold">
                        {
                          movement.newStock
                        }
                      </td>

                      <td className="py-4 text-[#746d65]">
                        {
                          movement.note ??
                          "—"
                        }
                      </td>
                    </tr>
                  )
                )}

                {movements.length ===
                  0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="py-12 text-center text-[#746d65]"
                    >
                      No inventory movements yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {selectedProduct && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-xl rounded-[2rem] bg-white p-7 shadow-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#70845b]">
              Inventory adjustment
            </p>

            <h2 className="mt-2 font-serif text-3xl text-[#38251e]">
              {
                selectedProduct.name
              }
            </h2>

            <p className="mt-2 text-sm text-[#746d65]">
              Current stock:
              {" "}
              <strong>
                {
                  selectedProduct
                    .stockQuantity
                }
              </strong>
            </p>

            <div className="mt-6 grid gap-5">
              <label>
                <span className="mb-2 block text-sm font-semibold text-[#38251e]">
                  Quantity change
                </span>

                <input
                  type="number"
                  step="1"
                  value={
                    quantityChange
                  }
                  onChange={(event) =>
                    setQuantityChange(
                      event.target.value
                    )
                  }
                  className="w-full rounded-xl border border-[#5e3b2c]/15 px-4 py-3"
                />

                <p className="mt-2 text-xs text-[#746d65]">
                  Use a positive number
                  to add stock and a negative
                  number to reduce stock.
                </p>
              </label>

              <label>
                <span className="mb-2 block text-sm font-semibold text-[#38251e]">
                  Reason
                </span>

                <select
                  value={reason}
                  onChange={(event) =>
                    setReason(
                      event.target
                        .value as AdminInventoryReason
                    )
                  }
                  className="w-full rounded-xl border border-[#5e3b2c]/15 px-4 py-3"
                >
                  {reasons.map(
                    (item) => (
                      <option
                        key={
                          item.value
                        }
                        value={
                          item.value
                        }
                      >
                        {
                          item.label
                        }
                      </option>
                    )
                  )}
                </select>
              </label>

              <label>
                <span className="mb-2 block text-sm font-semibold text-[#38251e]">
                  Note
                </span>

                <textarea
                  value={note}
                  onChange={(event) =>
                    setNote(
                      event.target.value
                    )
                  }
                  rows={3}
                  placeholder="Optional reason or reference"
                  className="w-full rounded-xl border border-[#5e3b2c]/15 px-4 py-3"
                />
              </label>

              {projectedStock !==
                null && (
                <div className={[
                  "rounded-2xl p-4",
                  projectedStock <
                  0
                    ? "bg-red-50 text-red-700"
                    : "bg-[#edf1e8] text-[#526746]"
                ].join(" ")}>
                  New stock:
                  {" "}
                  <strong>
                    {
                      projectedStock
                    }
                  </strong>
                </div>
              )}

              {adjustMutation.isError && (
                <div className="rounded-xl bg-red-50 p-4 text-sm text-red-700">
                  Inventory could not be updated.
                  Check the quantity and try again.
                </div>
              )}

              <div className="flex flex-wrap justify-end gap-3">
                <button
                  type="button"
                  onClick={() =>
                    setSelectedProduct(
                      null
                    )
                  }
                  className="rounded-full border border-[#5e3b2c]/15 px-5 py-3 text-sm font-semibold text-[#5e3b2c]"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={
                    adjustMutation.isPending ||
                    !quantityChange ||
                    Number(
                      quantityChange
                    ) === 0 ||
                    (
                      projectedStock !==
                        null &&
                      projectedStock <
                        0
                    )
                  }
                  onClick={() =>
                    adjustMutation.mutate()
                  }
                  className="rounded-full bg-[#5e3b2c] px-6 py-3 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {adjustMutation.isPending
                    ? "Saving..."
                    : "Save adjustment"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
